// AI Equipment Narrative API helper
// Communicates with Falcon backend /api/v1/ai-equipment-suggestions/enhancements/{id} endpoint
import { authenticatedJsonHeaders } from "./authenticatedHeaders";

export type AINarrativeStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed";

export interface AINarrativeContent {
  summary: string;
  pros: string[];
  cons: string[];
  watchItems: string[];
  rationale: string;
}

export interface AINarrativeResponse {
  success: boolean;
  enhancementId: string;
  status: AINarrativeStatus;
  pipelineVersion: string;
  narrativeVersion: string;
  // Present when status is 'completed'
  narrative?: AINarrativeContent;
  candidateNotes?: Record<
    string,
    {
      pros: string[];
      cons: string[];
      watch: string[];
    }
  >;
  missingData?: Array<{
    field: string;
    reason: string;
  }>;
  tokenUsage?: {
    prompt?: number;
    completion?: number;
  };
  latencyMs?: number;
  modelId?: string;
  cached?: boolean;
  // Present when status is 'failed'
  errorMessage?: string;
  error?: string;
}

/**
 * Error thrown when narrative fetch fails
 */
export class AINarrativeError extends Error {
  constructor(
    message: string,
    public status?: number,
    public responseText?: string,
  ) {
    super(message);
    this.name = "AINarrativeError";
  }
}

/**
 * Resolve the full URL for the enhancement endpoint
 */
function resolveBaseUrl(enhancementId: string): string {
  const explicitBase = (import.meta.env.VITE_FALCON_BASE || "").replace(
    /\/$/,
    "",
  );
  const path = `/api/v1/ai-equipment-suggestions/enhancements/${enhancementId}`;

  const isDev = import.meta.env.DEV;
  if (isDev && !explicitBase) return path;
  if (explicitBase) return `${explicitBase}${path}`;
  return path;
}

/**
 * Fetch the status and narrative for an enhancement job.
 *
 * @param enhancementId - The enhancement job ID returned from the suggestions endpoint
 * @returns Promise resolving to the enhancement response
 * @throws AINarrativeError if the request fails
 *
 * @example
 * ```typescript
 * const response = await fetchAINarrativeStatus('enh_abc123');
 * if (response.status === 'completed' && response.narrative) {
 *   console.log(response.narrative.summary);
 * }
 * ```
 */
export async function fetchAINarrativeStatus(
  enhancementId: string,
): Promise<AINarrativeResponse> {
  const url = resolveBaseUrl(enhancementId);

  try {
    const res = await fetch(url, {
      method: "GET",
      headers: await authenticatedJsonHeaders(),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new AINarrativeError(
        `AI narrative fetch failed: ${res.status}`,
        res.status,
        text,
      );
    }

    const data = await res.json();
    return data;
  } catch (error) {
    if (error instanceof AINarrativeError) {
      throw error;
    }
    // Network or parsing error
    throw new AINarrativeError(
      `Network error fetching narrative: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

/**
 * Check if the narrative is ready (completed or failed).
 * Useful for determining when to stop polling.
 */
export function isNarrativeReady(response: AINarrativeResponse): boolean {
  return response.status === "completed" || response.status === "failed";
}

/**
 * Check if the narrative completed successfully.
 */
export function isNarrativeComplete(response: AINarrativeResponse): boolean {
  return response.status === "completed";
}

/**
 * Check if the narrative is still being processed.
 */
export function isNarrativePending(response: AINarrativeResponse): boolean {
  return response.status === "pending" || response.status === "processing";
}

/**
 * Poll for narrative status with exponential backoff.
 * Automatically stops polling when narrative is ready.
 *
 * @param enhancementId - The enhancement job ID
 * @param options - Polling configuration
 * @returns Promise resolving to the final narrative response
 *
 * @example
 * ```typescript
 * const response = await pollNarrativeStatus('enh_abc123', {
 *   maxAttempts: 20,
 *   onProgress: (status) => console.log('Status:', status)
 * });
 * ```
 */
export async function pollNarrativeStatus(
  enhancementId: string,
  options: {
    initialDelay?: number;
    maxDelay?: number;
    maxAttempts?: number;
    backoffMultiplier?: number;
    onProgress?: (response: AINarrativeResponse) => void;
  } = {},
): Promise<AINarrativeResponse> {
  const {
    initialDelay = 2000,
    maxDelay = 30000,
    maxAttempts = 30,
    backoffMultiplier = 1.5,
    onProgress,
  } = options;

  let attempt = 0;
  let delay = initialDelay;

  while (attempt < maxAttempts) {
    const response = await fetchAINarrativeStatus(enhancementId);

    if (onProgress) {
      onProgress(response);
    }

    if (isNarrativeReady(response)) {
      return response;
    }

    attempt++;

    if (attempt >= maxAttempts) {
      throw new AINarrativeError(
        `Polling timeout after ${maxAttempts} attempts`,
      );
    }

    // Exponential backoff with max delay cap
    await new Promise((resolve) => setTimeout(resolve, delay));
    delay = Math.min(delay * backoffMultiplier, maxDelay);
  }

  throw new AINarrativeError(`Polling exceeded maximum attempts`);
}

/**
 * Extract user-friendly error message from response.
 */
export function getNarrativeErrorMessage(
  response: AINarrativeResponse,
): string | null {
  if (response.status !== "failed") return null;
  return (
    response.errorMessage || response.error || "Narrative generation failed"
  );
}
